import { useLatestBuild } from '@/hooks/useLatestBuild'
import { APP_URL, LOGIN_URL, MAC_BUILD, WAITLIST_PATH } from '@/lib/landing'
import { MacDownload } from './MacDownload'
import { CompatCheck } from './CompatCheck'
import { ButtonLink, card, Em, pad, Register, RegisterRow, SectionHead } from './primitives'

const DESKTOP = [
  ['Local models', 'Image, edit, video and music on your own GPU; 3D experimental'],
  ['Your agent', 'Plans on Claude, Grok, OpenAI, OpenRouter or local Ollama'],
  ['Sixty apps', 'Inpaint, erase, upscale, cutout, relight, animate'],
  ['Your account', 'Sign in once; credits and outputs sync with genie.art'],
] as const

const FIRST_LAUNCH = [
  'asks you to sign in to your Genie account',
  'installs Python, git and ComfyUI into ~/.genie — nothing else to set up',
  'scans chip, memory and free disk, then offers the models that fit',
  'downloads the models you pick, resumable, leaving 20 GB free',
] as const

export function Download() {
  const build = useLatestBuild()
  return (
    <section id="download" className="landing-shell scroll-mt-20 pt-28" aria-labelledby="download-heading">
      <SectionHead
        id="download-heading"
        eyebrow="Download"
        title={
          <>
            One download. <Em>Models on your Mac.</Em>
          </>
        }
        note="A macOS app for Apple Silicon. The first launch installs the engine and the models you pick, then generation runs on your own hardware."
      />

      <div className="grid gap-3 lg:grid-cols-2">
        <article className={`${card} flex min-w-0 flex-col gap-5 p-6 lg:p-8`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className="text-[24px] leading-none tracking-[-0.02em]">Genie for macOS</h3>
            <span className="mono-label text-ink-3">Apple Silicon</span>
          </div>
          <Register>
            {DESKTOP.map(([k, v]) => (
              <RegisterRow key={k} label={k} value={v} />
            ))}
          </Register>
          <div className="mt-auto flex flex-col gap-3 pt-2">
            <p className="mono-label text-ink-3">
              {build.file} · <span className="num">{build.size}</span> · {MAC_BUILD.requirements}
            </p>
            <MacDownload />
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[13px] text-ink-2">
              <span>Free forever · pay only for cloud credits</span>
              <a href={WAITLIST_PATH} className="text-ink underline decoration-rule-strong underline-offset-4 hover:decoration-ink">
                Windows: get notified
              </a>
            </div>
          </div>
        </article>

        <article className="flex min-w-0 flex-col gap-5 rounded-[3px] bg-dark p-6 text-on-dark lg:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className="text-[24px] leading-none tracking-[-0.02em]">Genie in the browser</h3>
            <span className="mono-label text-on-dark-2">No install</span>
          </div>
          <p className="text-[14px] leading-relaxed text-on-dark-2">
            Genie also runs at{' '}
            <a href={APP_URL} className="text-on-dark underline decoration-white/30 underline-offset-4 hover:decoration-white">
              app.genie.art
            </a>
            . Same account as the desktop app: your credits and synced outputs follow you.
          </p>
          <div className="mt-auto pt-2">
            <ButtonLink href={LOGIN_URL} variant="light" className="w-full">
              Sign in <span aria-hidden>→</span>
            </ButtonLink>
          </div>
        </article>
      </div>

      <CompatCheck />

      <div className="mt-3 grid gap-4 rounded-[3px] bg-panel px-6 py-5 md:grid-cols-[200px_1fr]">
        <span className="eyebrow">First launch</span>
        <ol className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
          {FIRST_LAUNCH.map((step, i) => (
            <li key={step} className="flex gap-3 text-[13px] text-ink-2">
              <span className="num text-[12px] text-ink-3" aria-hidden>
                {pad(i + 1)}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}
